/**
 * Unico punto da cui il frontend parla col backend.
 *
 * Concentrare qui le chiamate serve a tre cose: gestire allo stesso modo gli
 * errori, inviare sempre il cookie di sessione, e accorgersi in un posto solo
 * quando la sessione è scaduta.
 */

/** Errore che porta con sé lo stato HTTP e il codice del backend. */
export class ErroreApi extends Error {
  constructor(
    readonly stato: number,
    messaggio: string,
    readonly codice?: string,
    readonly dettagli?: { campo: string; messaggio: string }[],
  ) {
    super(messaggio);
    this.name = 'ErroreApi';
  }

  /** La sessione è scaduta o non c'è: chi ascolta deve rimandare al login. */
  get nonAutenticato(): boolean {
    return this.stato === 401;
  }
}

type Metodo = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/**
 * L'intestazione che dice al backend «questa richiesta viene dall'applicazione».
 *
 * 🔴 **Non è un segreto e non deve sembrarlo**: il suo valore è pubblico e
 * ininfluente. Quello che conta è che un `<form>` su un sito ostile **non possa
 * scriverla**: per aggiungere un'intestazione propria servono `fetch` o XHR, e
 * a quel punto il browser chiede prima il permesso al backend, che lo concede
 * solo alla nostra origine. È il secondo strato sotto `sameSite: 'lax'`.
 *
 * Il backend la pretende su POST, PUT, PATCH e DELETE — vedi
 * `backend/src/plugins/sicurezza.ts`. Toglierla di qui fa rispondere 403 a ogni
 * salvataggio dell'applicazione, quindi non passa inosservata.
 */
const INTESTAZIONE_PROVENIENZA = { 'X-Richiesta-FMSchedule': '1' } as const;

/**
 * Chi vuole sapere che la sessione è caduta si iscrive qui.
 *
 * Serve perché una risposta 401 può arrivare da qualunque chiamata, anche
 * mentre l'utente sta guardando tutt'altro: senza un punto centrale ogni
 * pagina dovrebbe gestirlo per conto suo, e prima o poi qualcuna se ne
 * dimenticherebbe lasciando l'utente davanti a una schermata vuota.
 */
const ascoltatoriSessioneScaduta = new Set<() => void>();

export function alDecaderedellaSessione(callback: () => void): () => void {
  ascoltatoriSessioneScaduta.add(callback);
  return () => ascoltatoriSessioneScaduta.delete(callback);
}

async function chiama<T>(
  metodo: Metodo,
  percorso: string,
  corpo?: unknown,
  annunciaSessioneScaduta = true,
): Promise<T> {
  const risposta = await fetch(`/api${percorso}`, {
    method: metodo,
    // Senza questo il cookie di sessione non parte, e ogni chiamata è anonima.
    credentials: 'same-origin',
    headers:
      corpo === undefined
        ? { ...INTESTAZIONE_PROVENIENZA }
        : { ...INTESTAZIONE_PROVENIENZA, 'Content-Type': 'application/json' },
    ...(corpo === undefined ? {} : { body: JSON.stringify(corpo) }),
  });

  if (risposta.status === 204) return undefined as T;

  const testo = await risposta.text();
  const dati: unknown = testo === '' ? null : JSON.parse(testo);

  if (!risposta.ok) {
    const e = dati as { errore?: string; codice?: string; dettagli?: unknown };

    if (risposta.status === 401 && annunciaSessioneScaduta) {
      for (const ascoltatore of ascoltatoriSessioneScaduta) ascoltatore();
    }

    throw new ErroreApi(
      risposta.status,
      e?.errore ?? `Errore ${risposta.status}`,
      e?.codice,
      Array.isArray(e?.dettagli)
        ? (e.dettagli as { campo: string; messaggio: string }[])
        : undefined,
    );
  }

  return dati as T;
}

export const api = {
  get: <T>(percorso: string) => chiama<T>('GET', percorso),

  /**
   * Come `get`, ma un 401 **non** annuncia che la sessione è caduta.
   *
   * Serve alle richieste **di fondo**: quelle che partono da sole, a
   * intervalli, senza che l'utente abbia toccato niente. La striscia del fermo
   * interroga ogni 30 secondi ed è la prima richiesta di questo genere
   * nell'applicazione. Passando dal canale globale, alla scadenza della
   * sessione l'utente si troverebbe riportato all'accesso entro mezzo minuto,
   * con un modulo compilato a metà smontato sotto le mani e senza aver fatto
   * un clic. Prima di questa striscia la sessione caduta si scopriva alla
   * prima azione vera, ed è lì che deve continuare a scoprirsi: una
   * funzionalità nata per non far perdere lavoro non può aprire un modo nuovo
   * di perderlo.
   *
   * L'errore viene comunque **sollevato**: tacere è solo il non annunciarlo
   * agli altri, e chi chiama decide cosa farne. La striscia tiene quello che
   * ha già a schermo e riprova al giro dopo — che è anche il comportamento
   * giusto quando il backend non risponde affatto.
   *
   * ⚠️ Regola per chi aggiunge una chiamata, e il criterio **non** è «parte
   * senza un gesto dell'utente»: si usa `getDiFondo` quando l'unica ragione
   * della richiesta è tenere aggiornata un'informazione di contorno, e
   * scoprire lì che la sessione è caduta smonterebbe una schermata che
   * qualcuno sta guardando o compilando.
   *
   * Il criterio del gesto sarebbe sbagliato, e romperebbe una cosa che
   * funziona: anche `autenticazione.io()` parte da sola — il contesto la
   * rilancia al ritorno sulla finestra e a ogni cambio di rotta — ma lì
   * l'annuncio è **voluto**, ed è il meccanismo su cui si regge il congedo
   * dopo il cambio di password (vedi `auth/congedo.ts`). Quella deve
   * continuare a passare da `get`.
   *
   * Chi passa una chiamata a `getDiFondo` si prende anche l'altra metà del
   * patto: gestire l'errore sul posto. Nessuno lo farà al posto suo.
   */
  getDiFondo: <T>(percorso: string) => chiama<T>('GET', percorso, undefined, false),
  post: <T>(percorso: string, corpo?: unknown) => chiama<T>('POST', percorso, corpo),
  patch: <T>(percorso: string, corpo: unknown) => chiama<T>('PATCH', percorso, corpo),
  put: <T>(percorso: string, corpo: unknown) => chiama<T>('PUT', percorso, corpo),

  /**
   * Cancellazione definitiva, con un corpo facoltativo.
   *
   * Il corpo serve quando la cancellazione **non ha una sola forma**: per
   * soggetti e tipi si manda `{ collegate: 'ELIMINA' | 'SCOLLEGA' }`, cioè che
   * fine devono fare le scadenze attaccate. Non sta nell'URL di proposito —
   * è la scelta più distruttiva dell'applicazione, e una query string si copia,
   * si mette fra i preferiti e si ripete per sbaglio.
   *
   * ⚠️ Il corpo è facoltativo *qui*, non nel contratto: quando l'impatto
   * riporta `collegate` diverso da `null` il backend rifiuta con 400 la DELETE
   * senza scelta, proprio perché il ramo distruttivo non possa partire per
   * omissione. Ometterlo è legittimo solo per scadenze e utenti.
   *
   * Le rotte di eliminazione di questa applicazione **rispondono 200 con
   * l'elenco di ciò che è stato distrutto**, non 204 — chi ha appena confermato
   * ha diritto di leggere cosa è successo davvero. Il 204 lo gestisce comunque
   * `chiama` qui sopra, quindi se un giorno una rotta smettesse di restituire il
   * riepilogo questa funzione non si romperebbe.
   */
  delete: <T>(percorso: string, corpo?: unknown) => chiama<T>('DELETE', percorso, corpo),

  /**
   * Caricamento di un file.
   *
   * ⚠️ **Non si imposta `Content-Type`**, ed è l'unico punto dell'applicazione
   * dove ometterlo è obbligatorio: `multipart/form-data` richiede un
   * *boundary*, una stringa di separazione che il browser genera da sé e
   * infila nell'intestazione. Scrivendola a mano il boundary manca, e il
   * server riceve un corpo che non sa dividere.
   *
   * Non passa da `chiama` perché quella funzione serializza in JSON: qui il
   * corpo è un `FormData` e va lasciato stare com'è.
   */
  carica: async <T>(percorso: string, file: File): Promise<T> => {
    const modulo = new FormData();
    modulo.append('file', file);

    const risposta = await fetch(`/api${percorso}`, {
      method: 'POST',
      credentials: 'same-origin',
      // Solo l'intestazione di provenienza: `Content-Type` resta al browser,
      // per la ragione del boundary spiegata qui sopra.
      headers: { ...INTESTAZIONE_PROVENIENZA },
      body: modulo,
    });

    const testo = await risposta.text();
    const dati: unknown = testo === '' ? null : JSON.parse(testo);

    if (!risposta.ok) {
      const e = dati as { errore?: string; codice?: string };
      if (risposta.status === 401) {
        for (const ascoltatore of ascoltatoriSessioneScaduta) ascoltatore();
      }
      throw new ErroreApi(risposta.status, e?.errore ?? `Errore ${risposta.status}`, e?.codice);
    }

    return dati as T;
  },
};

/** Compone una query string saltando i valori vuoti. */
export function query(parametri: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams();
  for (const [chiave, valore] of Object.entries(parametri)) {
    if (valore !== undefined && valore !== '') p.set(chiave, String(valore));
  }
  const s = p.toString();
  return s === '' ? '' : `?${s}`;
}
